// @ts-check

import { entryCountry } from '@/core/constants'
import cleanSource from '@/helpers/clean-source'
import { incomeTypeSales } from '@/helpers/entryAssignmentOptions'

/**
 * @type {{
 *    IDLE: 'IDLE',
 *    LOADING: 'LOADING',
 *    READY: 'READY',
 *    ERROR: 'ERROR'
 *    INIT: 'INIT'
 * }}
 */
export const ocrState = {
  IDLE: 'IDLE',
  INIT: 'INIT',
  LOADING: 'LOADING',
  READY: 'READY',
  ERROR: 'ERROR'
}

/**
 * @param {IncomeItemResource} itemObject
 */
const serializeIncomeItem = (itemObject) => {
  const item = cleanSource(itemObject)

  // remove empty keys
  Object.keys(item).forEach((key) => {
    if (item[key] === null || item[key] === undefined) {
      delete item[key]
    }
  })

  if (item.posting_type === incomeTypeSales) {
    if (item.country !== entryCountry.EU) {
      delete item.buyer_country
      delete item.buyer_vat_number
    }
  } else {
    delete item.country
    delete item.buyer_type
    delete item.buyer_country
    delete item.buyer_vat_number
  }

  return item
}

/**
 * Serializes assignment to format accepted by API.
 */
export const serializeIncome = (assignment) => {
  const income = cleanSource(assignment)

  if (Array.isArray(income?.items)) {
    income.items = income.items.map((item) => {
      return serializeIncomeItem(item)
    })
  }

  if (assignment.hasPaymentReductions) {
    income.payment_reductions = assignment.paymentReductions
  }

  delete income.hasPaymentReductions
  delete income.paymentReductions

  return income
}

export const serializeExpense = (assignment = {}, supplier = {}) => {
  const expense = cleanSource({ ...assignment })
  delete expense.supplier

  if (Array.isArray(expense?.items)) {
    expense.items = expense.items.map(({ ...item }) => {
      if (item.posting_type !== 'product' && item.posting_type !== 'service') {
        delete item.business_related
      }
      item.country = supplier.country
      return item
    })
  }
  if (supplier.existingId) {
    expense.supplier_id = supplier.existingId
  }

  if (supplier.businessId) {
    expense.supplier_business_id = supplier.businessId
  }

  if (supplier.name !== null && supplier.name !== undefined) {
    expense.supplier_name = supplier.name
  }

  if (supplier.finalType !== null && supplier.finalType !== undefined) {
    expense.supplier_type = supplier.finalType
  }

  return expense
}

/**
 * Serializes all state specific data from the assignment.
 * @param {EntryGetters} assignment
 */
export const serialize = ({ assignment = {}, supplier }) => {
  let serialized = { ...assignment }

  if (assignment.type === 'expense') {
    serialized = serializeExpense(serialized, supplier)
  } else if (assignment.type === 'income') {
    serialized = serializeIncome(serialized)
  }

  delete serialized.supplier
  delete serialized.type
  delete serialized.files
  delete serialized.paymentReductions
  delete serialized.hasPaymentReductions

  return serialized
}

/**
 * @returns {PaymentReductionResource}
 */
export const createPaymentReduction = () => ({
  amount: null,
  type: null,
  vat: null,
  country: null
})

const incomeFields = {
  buyer_vat_number: null,
  buyer_type: null,
  buyer_country: null
}

const expenseFields = {
  business_related: null
}

/**
 * @param {EntryType} entryType
 */
export const createPostingRow = (entryType, country = null) => {
  const commonFields = {
    price: null,
    vat: null,
    posting_type: null,
    // @NOTE: posting_sub_type might have to be renamed later once API gets finished
    posting_subtype: null,
    country,
    dimension_cost_center: null,
    dimension_project: null,
    dimension_person: null,
    description: null
  }

  switch (entryType) {
    case 'income':
      return { ...commonFields, ...incomeFields }
    case 'expense':
      return { ...commonFields, ...expenseFields }
    default:
      throw new Error(`Unknown entry type: ${entryType}`)
  }
}

export default {
  ocrState,
  serializeExpense,
  serializeIncome,
  createPostingRow
}
